import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { SettingsIcon } from 'components/icons';
import { BackButton, MENU_ICONS, SideMenuLink } from 'components/ui';
import { Drawer } from 'components/ui/Drawer';
import { useIsAdmin, useIsFullWidth, useIsOrganizationLeader } from 'hooks';
import { MainLayout } from 'layouts/MainLayout';
import Head from 'next/head';
import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Routes } from 'services/routes';
import { Image } from 'components/lib';

export interface MasterPanelPageLayoutProps {
  children?: ReactNode;
  title?: string;
  actions?: ReactNode;
  active:
    | 'schools'
    | 'majors'
    | 'organizations'
    | 'users'
    | 'events'
    | 'tickets'
    | 'configurations'
    | 'help'
    | string;
}

export const MasterPanelPageLayout = ({
  children,
  title,
  active,
  actions,
}: MasterPanelPageLayoutProps): ReactElement => {
  const isOrganizationLeader = useIsOrganizationLeader();
  const isAdmin = useIsAdmin();

  const isFullWidth = useIsFullWidth();
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const navContent = useMemo(
    () => (
      <Box
        height="100%"
        width="100%"
        borderRight={isFullWidth ? '1px solid #dedede' : 'none'}
      >
        <Typography
          variant="h2"
          color="primary"
          sx={{
            color: theme => theme.palette.text.primary,
            fontSize: pxToEm(22),
            fontWeight: 500,
            p: '48px 20px 7px',
          }}
        >
          Master Panel
        </Typography>
        <Box
          component="nav"
          role="navigation"
          p={0}
          display="flex"
          flexDirection="column"
        >
          <SideMenuLink
            icon={
              <Image
                width={24}
                alt={'Schools'}
                src={
                  active === 'schools'
                    ? MENU_ICONS.school
                    : MENU_ICONS.schoolOutline
                }
              />
            }
            href={Routes.master.schools()}
            label="Schools"
            active={active === 'schools'}
          />
          <SideMenuLink
            icon={
              <Image
                width={24}
                alt={'Majors'}
                src={
                  active === 'majors'
                    ? MENU_ICONS.analytics
                    : MENU_ICONS.analyticsOutline
                }
              />
            }
            href={Routes.master.majors()}
            label="Majors"
            active={active === 'majors'}
          />
          <SideMenuLink
            icon={
              <Image
                width={24}
                alt={'Organizations'}
                src={
                  active === 'organizations'
                    ? MENU_ICONS.organization
                    : MENU_ICONS.organizationOutline
                }
              />
            }
            href={Routes.master.organizations()}
            label="Organizations"
            active={active === 'organizations'}
          />
          <SideMenuLink
            icon={
              <Image
                width={24}
                alt={'Users'}
                src={
                  active === 'users'
                    ? MENU_ICONS.users
                    : MENU_ICONS.usersOutline
                }
              />
            }
            href={Routes.master.users()}
            label="Users"
            active={active === 'users'}
          />
          <SideMenuLink
            icon={
              <Image
                width={24}
                alt={'Events'}
                src={
                  active === 'events'
                    ? MENU_ICONS.eventsSettings
                    : MENU_ICONS.eventsSettingsOutline
                }
              />
            }
            href={Routes.master.events()}
            label="Events"
            active={active === 'events'}
          />
          {/* <SideMenuLink
            icon={<Image width={24} alt={'Tickets'} src={active === 'tickets' ? MENU_ICONS.tickets : MENU_ICONS.ticketsOutline} />}
            href={Routes.master.tickets()}
            label="Tickets"
            active={active === 'tickets'}
          /> */}
          <SideMenuLink
            icon={<SettingsIcon />}
            href={Routes.master.configurations()}
            label="Configurations"
            active={active === 'configurations'}
          />
        </Box>
      </Box>
    ),
    [active, isOrganizationLeader, isAdmin, isFullWidth],
  );

  return (
    <MainLayout>
      <Head>
        <title>{title}</title>
      </Head>

      {isFullWidth && (
        <MainLayout.Sidebar side="left" fixed>
          {navContent}
        </MainLayout.Sidebar>
      )}

      {!isFullWidth && (
        <Drawer
          title="Admin Portal"
          onOpen={() => setDrawerOpen(true)}
          onClose={closeDrawer}
          open={isDrawerOpen}
        >
          {navContent}
        </Drawer>
      )}

      {(title || actions) && (
        <Box mt={isFullWidth ? 0 : 26} display="flex" alignItems="center">
          {!isFullWidth && (
            <Box mr={10}>
              <BackButton onClick={() => setDrawerOpen(true)} />
            </Box>
          )}
          <Typography
            variant="h2"
            style={{ fontWeight: 700, fontSize: isFullWidth ? 30 : 20 }}
          >
            {title}
          </Typography>
          <Box ml={30}>{actions}</Box>
        </Box>
      )}
      {children}
    </MainLayout>
  );
};
